import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, Box, Card, CardActionArea, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Request } from '../types';

interface RequestCardProps {
	request: Request;
	isSelected: boolean;
	onClick: (requestId: string) => void;
	isMobile?: boolean;
}

export const RequestCard: React.FC<RequestCardProps> = ({
	request,
	isSelected,
	onClick,
	isMobile,
}) => {
	const { request_id, crops, request_type, days_remaining, start_date, end_date, offers_count, original_date, payment_status } =
		request;
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();

	const requestTypeTitle =
		request_type === 'SEASONAL'
			? t('findSpecialistPage.seasonalConsultation')
			: t('findSpecialistPage.oneTimeConsultation');
			
	// Determine which dates to display
	const displayStartDate = useMemo(() => {
		if (payment_status === 'unpaid' && original_date?.start_date) {
			return original_date.start_date;
		}
		return start_date;
	}, [payment_status, original_date, start_date]);

	const displayEndDate = useMemo(() => {
		if (payment_status === 'unpaid' && original_date?.end_date) {
			return original_date.end_date;
		}
		return end_date;
	}, [payment_status, original_date, end_date]);
			
	// Calculate days remaining based on end_date minus current date
	const daysRemaining = useMemo(() => {
		// Use the regular end_date for calculation, not the display end date
		if (end_date) {
			try {
				let endDateObj;
				
				// Parse end_date based on format
				if (typeof end_date === 'string') {
					if (end_date.includes('.')) {
						const [day, month, year] = end_date.split('.');
						endDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
					} else {
						endDateObj = new Date(end_date);
					}
				}
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date:', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to days
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Days remaining:', diffDays);
					
					// Return positive days or 0 if expired
					return Math.max(0, diffDays);
				}
			} catch (error) {
				console.error('Error calculating days remaining:', error);
			}
		}
		
		// Use days_remaining if available, otherwise default to 0
		return typeof days_remaining === 'number' && !isNaN(days_remaining) ? days_remaining : 0;
	}, [end_date, days_remaining]);
			
	// Calculate total consultation period (days between start_date and end_date)
	const totalConsultationDays = useMemo(() => {
		if (start_date && end_date) {
			try {
				let startDateObj, endDateObj;
				
				// Parse start_date
				if (typeof start_date === 'string') {
					if (start_date.includes('.')) {
						const [day, month, year] = start_date.split('.');
						startDateObj = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
					} else {
						startDateObj = new Date(start_date);
					}
				}
				
				// Parse end_date
				if (typeof end_date === 'string') {
					if (end_date.includes('.')) {
						const [day, month, year] = end_date.split('.');
						endDateObj = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
					} else {
						endDateObj = new Date(end_date);
					}
				}
				
				// Calculate days between start_date and end_date
				if (startDateObj instanceof Date && !isNaN(startDateObj.getTime()) &&
					endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					
					const diffTime = endDateObj.getTime() - startDateObj.getTime();
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					return Math.max(0, diffDays);
				}
			} catch (error) {
				console.error('Error calculating consultation period:', error);
			}
		}
		
		// Use days_remaining if available, otherwise default to 0
		return typeof days_remaining === 'number' && !isNaN(days_remaining) ? days_remaining : 0;
	}, [start_date, end_date, days_remaining]);

	const navigateToDetailedPage = () => {
		navigateWithLanguage(`/farmer/seasonal-onetime/offers/${request.id}`);
	};

	return (
		<Card
			sx={{
				mb: 2,
				borderRadius: '8px',
				boxShadow: 'none',
				backgroundColor: isSelected ? '#F5EFE9' : 'white',
				transition: 'all 0.2s ease',
				'&:hover': {
					backgroundColor: '#F5EFE9',
					boxShadow: isSelected ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : 'none',
				},
			}}
		>
			<CardActionArea
				onClick={() => onClick(request_id || request.id)}
				sx={{
					display: 'flex',
					justifyContent: 'flex-start',
					textAlign: 'left',
					padding: 2,
				}}
			>
				{/* Crop Image */}
				<Avatar
					src={crops[0]?.image_url}
					alt={crops[0]?.name}
					sx={{ width: 64, height: 64, borderRadius: '8px' }}
				/>

				{/* Request Details */}
				<Box sx={{ ml: 2, flex: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, fontSize: '1rem' }}>
						{crops[0]?.name}
					</Typography>

					{/* Request Type */}
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							mb: 1,
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{requestTypeTitle}
					</Typography>

					{/* Date Range */}
					<Typography
						variant="body2"
						sx={{ 
							mb: 1, 
							fontSize: '0.875rem',
							color: 'text.secondary',
							fontWeight: 400
						}}
					>
						{displayStartDate && displayEndDate ? `${displayStartDate} - ${displayEndDate}` : ''}
					</Typography>

					<Box
						sx={{
							display: 'flex',
							gap: 1,
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<div className="flex gap-1 items-center">
							<span className="text-[#7B809A] text-[14px] leading-[140%] max-xsm:text-[12px]">
								{t('activeForDays', { days: daysRemaining })}
							</span>
						</div>
					</Box>
				</Box>
				<div className="flex flex-col gap-1">
					<Typography
						onClick={navigateToDetailedPage}
						variant="caption"
						sx={{
							color: '#00AB89',
							textTransform: 'uppercase',
							fontSize: '16px',
							fontWeight: 500,
							textDecoration: 'underline',
						}}
					>
						#{request_id?.toString().substring(0, 6) || request.id.toString().substring(0, 6)}
					</Typography>
					<span className="text-secondary roboto-bold text-[16px] leading-[140%]">
						{offers_count}
						<span className="ml-[2px]">{t('offers')}</span>
					</span>
				</div>

				{/* Expansion indicator for mobile */}
				{isMobile && (
					<Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
						{isSelected ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</Box>
				)}
			</CardActionArea>
		</Card>
	);
};
